import React, { useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faClipboardCheck,
  faClose,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { MessageList } from "../../../components/MessageList/MessageList"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useChecklist } from "../../../hooks/Checklist/useChecklist"
import { useError } from "../../../context/useErrorContext"
import { useConfirmation } from "../../../context/useConfirmationContext"
import type { ChecklistTemplate } from "../../../Objects/Checklist/ChecklistTemplate"
import type { SaveChecklistTemplate } from "../../../Objects/Checklist/SaveChecklistTemplate"
import type { ChecklistSector } from "../../../Objects/Checklist/ChecklistSector"
import type { SaveChecklistTemplateStep } from "../../../Objects/Checklist/SaveChecklistTemplateStep"
import { EWeekDays, WEEKDAY_ORDER } from "../../../Objects/Checklist/EWeekDays"
import { employeeMemory } from "../../../Objects/Memory/Memory"
import { ChecklistTemplateStepPopup } from "./ChecklistTemplateStepPopup"

interface Props {
  template: ChecklistTemplate | null
  sectors: ChecklistSector[]
  onClose: () => void
  onSaved: () => void
}

const parseScheduledWeekdays = (value?: string | null): number => {
  if (!value) return 0

  return value
    .split(",")
    .map((part) => part.trim())
    .reduce((mask, name) => mask | (EWeekDays[name as keyof typeof EWeekDays] ?? 0), 0)
}

const formatScheduledWeekdays = (mask: number): string =>
  WEEKDAY_ORDER.filter((day) => mask & day.value)
    .map((day) => Object.keys(EWeekDays).find((key) => EWeekDays[key as keyof typeof EWeekDays] === day.value))
    .filter((name): name is string => Boolean(name))
    .join(", ")

export const ChecklistTemplatePopup: React.FC<Props> = ({ template, sectors, onClose, onSaved }) => {
  const { t } = useTranslation(["pages", "general", "helpPopup"])

  const { saveTemplate, activateTemplate, deactivateTemplate, deleteTemplate, isLoading } = useChecklist()

  const { setError } = useError()

  const { setNewConfirmation } = useConfirmation()

  const [name, setName] = useState(template?.name ?? "")

  const [description, setDescription] = useState(template?.description ?? "")

  const [sectorId, setSectorId] = useState<string>(template?.sectorId ? String(template.sectorId) : "")

  const [isMandatory, setIsMandatory] = useState(template?.isMandatory ?? false)

  const [requireOrderedSteps, setRequireOrderedSteps] = useState(template?.requireOrderedSteps ?? false)

  const [frequencyType, setFrequencyType] = useState<ChecklistTemplate["frequencyType"]>(
    template?.frequencyType ?? "None",
  )

  const [scheduledWeekdaysMask, setScheduledWeekdaysMask] = useState<number>(() =>
    parseScheduledWeekdays(template?.scheduledWeekdays),
  )

  const [scheduledDayOfMonth, setScheduledDayOfMonth] = useState(template?.scheduledDayOfMonth ?? 1)

  const [deadlineTime, setDeadlineTime] = useState(template?.deadlineTime?.slice(0, 5) ?? "")

  const [responsibleEmployeeId, setResponsibleEmployeeId] = useState(template?.responsibleEmployeeId ?? "")

  const [steps, setSteps] = useState<SaveChecklistTemplateStep[]>(
    template?.steps.map((step) => ({
      order: step.order,
      title: step.title,
      description: step.description,
      responseType: step.responseType,
      isRequired: step.isRequired,
      responsibleEmployeeId: step.responsibleEmployeeId,
      options: step.options,
    })) ?? [],
  )

  const [stepPopup, setStepPopup] = useState<{ index: number | null } | null>(null)

  const [stepsExpanded, setStepsExpanded] = useState(true)

  const [isActive, setIsActive] = useState(template?.isActive ?? true)

  const [isDraftClone, setIsDraftClone] = useState(false)

  const isExistingTemplate = Boolean(template) && !isDraftClone

  const toggleWeekday = (value: number) =>
    setScheduledWeekdaysMask((current) => (current & value ? current & ~value : current | value))

  const removeStep = (index: number) =>
    setSteps((current) => current.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i })))

  const handleSaveStep = (updated: SaveChecklistTemplateStep) => {
    if (stepPopup?.index !== null && stepPopup?.index !== undefined) {
      const index = stepPopup.index

      setSteps((current) => current.map((step, i) => (i === index ? { ...updated, order: index } : step)))
    } else {
      setSteps((current) => [...current, { ...updated, order: current.length }])
    }
  }

  const moveStep = (index: number, direction: -1 | 1) => {
    const target = index + direction

    if (target < 0 || target >= steps.length) return

    const updated = [...steps]

    const [moved] = updated.splice(index, 1)

    updated.splice(target, 0, moved)

    setSteps(updated.map((step, i) => ({ ...step, order: i })))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (steps.length === 0) {
      setError(t("general:label_title_erro"), t("checklists.templates.stepsRequiredError"))
      return
    }

    const request: SaveChecklistTemplate = {
      id: isExistingTemplate ? (template?.id ?? null) : null,
      name,
      description: description || null,
      sectorId: sectorId ? Number(sectorId) : null,
      isMandatory,
      requireOrderedSteps,
      frequencyType,
      scheduledWeekdays:
        frequencyType === "Weekly" || frequencyType === "Custom"
          ? formatScheduledWeekdays(scheduledWeekdaysMask)
          : null,
      scheduledDayOfMonth: frequencyType === "Monthly" ? scheduledDayOfMonth : null,
      deadlineTime: deadlineTime ? `${deadlineTime}:00` : null,
      responsibleEmployeeId: responsibleEmployeeId || null,
      steps,
    }

    const result = await saveTemplate(request)

    if (!result) return

    if (isExistingTemplate && template && isActive !== template.isActive) {
      const statusResult = isActive ? await activateTemplate(template.id) : await deactivateTemplate(template.id)

      if (!statusResult) return
    }

    onSaved()
    onClose()
  }

  const handleClone = () => setIsDraftClone(true)

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setIsActive(event.target.selectedIndex === EEnableStatusType.Active)
  }

  const handleDelete = () => {
    if (!isExistingTemplate || !template) return

    setNewConfirmation(
      t("checklists.templates.confirmDeleteTitle"),
      t("checklists.templates.confirmDeleteMessage", { name: template.name }),
      () => {
        void (async () => {
          const result = await deleteTemplate(template.id)

          if (result) {
            onSaved()
            onClose()
          }
        })()
      },
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={isExistingTemplate ? t("checklists.templates.editTitle") : t("checklists.templates.createTitle")}
              icon={faClipboardCheck}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {isExistingTemplate && (
                    <div style={{ width: "100px", marginRight: "5px" }}>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                        initialState={isActive ? EEnableStatusType.Active : EEnableStatusType.Inactive}
                        disable={isLoading}
                        handlerFuncion={handleStatusChange}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.select.status"
                      />
                    </div>
                  )}

                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.checklist-templates"
                    title={t("helpPopup:global.checklistTemplates.title")}
                    description={t("helpPopup:global.checklistTemplates.description")}
                    bullets={t("helpPopup:global.checklistTemplates.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.onClose"
                  />
                </div>
              }
            />

            {isDraftClone && <CopyDraftAlert />}

            <div className={defaultStyle.divisionBar}></div>

            <form onSubmit={handleSubmit} className="p-2">
              <InputField
                type="text"
                icon={faClipboardCheck}
                value={name}
                placeholder={t("checklists.templates.namePlaceholder")}
                onInvalidMessage={t("checklists.templates.namePlaceholder")}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={150}
                descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.input.name"
              />

              <GetSpace top={10} />

              <InputField
                type="text"
                value={description}
                placeholder={t("checklists.templates.descriptionPlaceholder")}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.input.description"
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className="gap-2 space-y-2">
                <GenericDropDown
                  data={[
                    { label: t("checklists.templates.noSector"), value: "" },
                    ...sectors.map((sector) => ({ label: sector.name, value: String(sector.id) })),
                  ]}
                  initialState={sectorId}
                  handlerFuncion={(e) => setSectorId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.select.sector"
                />

                <GenericDropDown
                  data={[
                    { label: t("checklists.templates.noResponsible"), value: "" },
                    ...employeeMemory.listData.map((employee) => ({ label: employee.userName, value: employee.id })),
                  ]}
                  initialState={responsibleEmployeeId}
                  handlerFuncion={(e) => setResponsibleEmployeeId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.select.responsible"
                />
              </div>

              <GetSpace top={10} />

              <div className="gap-2 space-y-2">
                <SimpleToggle
                  title={t("checklists.templates.isMandatory")}
                  value={isMandatory}
                  onChange={setIsMandatory}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.toggle.mandatory"
                />

                <SimpleToggle
                  title={t("checklists.templates.requireOrderedSteps")}
                  value={requireOrderedSteps}
                  onChange={setRequireOrderedSteps}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.toggle.orderedSteps"
                />
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <div className="gap-2 space-y-2">
                <GenericDropDown
                  data={[
                    { label: t("checklists.templates.frequencyNone"), value: "None" },
                    { label: t("checklists.templates.frequencyDaily"), value: "Daily" },
                    { label: t("checklists.templates.frequencyWeekly"), value: "Weekly" },
                    { label: t("checklists.templates.frequencyMonthly"), value: "Monthly" },
                    { label: t("checklists.templates.frequencyCustom"), value: "Custom" },
                  ]}
                  initialState={frequencyType}
                  handlerFuncion={(e) => setFrequencyType(e.target.value as typeof frequencyType)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.select.frequency"
                />

                <InputField
                  type="time"
                  value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)}
                  placeholder={t("checklists.templates.deadlineTime")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.input.deadlineTime"
                />
              </div>

              {(frequencyType === "Weekly" || frequencyType === "Custom") && (
                <>
                  <GetSpace top={10} />

                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_ORDER.map((day) => (
                      <Animate key={day.value} duration="1.5s">
                        <button
                          type="button"
                          onClick={() => toggleWeekday(day.value)}
                          className="px-3 py-2 rounded text-sm font-semibold border transition-all"
                          style={
                            scheduledWeekdaysMask & day.value
                              ? {
                                  backgroundColor: THEME.colors.buttonBGSaveColor,
                                  borderColor: THEME.colors.buttonBGSaveColor,
                                }
                              : { backgroundColor: THEME.colors.white, borderColor: "#d1d5db" }
                          }
                        >
                          {t(`checklists.templates.${day.labelKey}`)}
                        </button>
                      </Animate>
                    ))}
                  </div>
                </>
              )}

              {frequencyType === "Monthly" && (
                <>
                  <GetSpace top={10} />

                  <InputField
                    type="number"
                    value={String(scheduledDayOfMonth)}
                    onChange={(e) => setScheduledDayOfMonth(Number(e.target.value))}
                    placeholder={t("checklists.templates.dayOfMonth")}
                    min={1}
                    max={31}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.input.dayOfMonth"
                  />
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <TitlePageResponsiveIconSmall
                title={t("checklists.templates.stepsTitle")}
                icon={faClipboardCheck}
                iconColor={THEME.colors.iconBGColor}
                boxShadow="none"
                sizeProp="sm"
                children={
                  <div className="flex items-center gap-1 shrink-0">
                    <ButtonIcon
                      sizeNumber={32}
                      bgColor={THEME.colors.buttonBGPlusColor}
                      icon={faPlus}
                      onClick={() => setStepPopup({ index: null })}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.addStep"
                    />

                    <ButtonIcon
                      sizeNumber={32}
                      bgColor={THEME.colors.buttonBGExpandColor}
                      icon={stepsExpanded ? faChevronUp : faChevronDown}
                      onClick={() => setStepsExpanded((current) => !current)}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.toggleStepsExpanded"
                    />
                  </div>
                }
              />

              {steps.length === 0 && (
                <MessageList
                  color={THEME.colors.cardMessageNoItem}
                  icon={faClipboardCheck}
                  message={t("checklists.templates.stepsEmpty")}
                />
              )}

              {stepsExpanded && steps.length > 0 && (
                <div className="flex flex-col gap-2 mt-2 max-h-[45vh] overflow-y-auto">
                  {steps.map((step, index) => (
                    <Animate duration="1.5s">
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 border border-gray-200 transition-all cursor-pointer"
                        onClick={() => setStepPopup({ index })}
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center shrink-0 text-sm font-bold">
                          {index + 1}
                        </div>

                        <div className="min-w-0 flex-1">
                          <span className="font-semibold text-gray-800 truncate block">
                            {step.title || t("checklists.templates.stepTitlePlaceholder")}
                          </span>

                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="px-2 py-0.5 text-[11px] rounded-full bg-gray-200 text-gray-600 font-semibold">
                              {t(`checklists.templates.responseType${step.responseType}`)}
                            </span>

                            {step.isRequired && (
                              <span className="px-2 py-0.5 text-[11px] rounded-full bg-red-100 text-red-600 font-semibold">
                                {t("checklists.templates.mandatoryTag")}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <ButtonIcon
                            sizeNumber={28}
                            icon={faArrowUp}
                            onClick={() => moveStep(index, -1)}
                            disabled={index === 0}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.moveStepUp"
                          />

                          <ButtonIcon
                            sizeNumber={28}
                            icon={faArrowDown}
                            onClick={() => moveStep(index, 1)}
                            disabled={index === steps.length - 1}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.moveStepDown"
                          />

                          <ButtonIcon
                            sizeNumber={28}
                            bgColor={THEME.colors.buttonBGRemoveColor}
                            icon={faTrash}
                            onClick={() => removeStep(index)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.removeStep"
                          />
                        </div>
                      </div>
                    </Animate>
                  ))}
                </div>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={t("general:label_save")}
                  typeButton="submit"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={isLoading}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.save"
                />

                {isExistingTemplate && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("general:label_clone")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={handleClone}
                      disabled={isLoading}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.clone"
                    />

                    <GetSpace right={5} />

                    <Button
                      label={t("general:label_delete")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={handleDelete}
                      disabled={isLoading}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistTemplatePopup.button.delete"
                    />
                  </>
                )}
              </div>
            </form>
          </section>
        </Animate>
      </div>

      {stepPopup && (
        <ChecklistTemplateStepPopup
          step={stepPopup.index !== null ? steps[stepPopup.index] : null}
          onSave={handleSaveStep}
          onClose={() => setStepPopup(null)}
        />
      )}
    </ModalPortal>
  )
}
